import {
  HTS_ANSWER_CLASSIFICATION_LABELS,
  HtsAnswerClassification,
  HtsFlagPriority,
  HtsReadinessPillarKey,
  HtsSubmissionSummaryStatus,
} from "./constants";
import { isSuitabilityTopic } from "./htsClassificationTopics";
import {
  HTS_READINESS_PILLAR_CONFIG_BY_KEY,
  HtsEducationDimensionKey,
  HtsFitnessDimensionKey,
  HtsIntegrityDimensionKey,
} from "./htsReadinessPillars";

/**
 * Pure, I/O-free scoring pieces shared between the API (htsReadinessScoring.ts,
 * the authoritative source) and the admin review page's live client-side
 * recompute — one implementation both import, rather than restating the formula
 * in the frontend or calling the API on every edit.
 */

/**
 * Whether a Readiness Profile is still waiting on an admin's sign-off — the
 * mandatory-review gate, in one place. Guest-agnostic: this is the admin's
 * "Needs review" state as much as the candidate's gate.
 *
 * Once released, a profile is never pending again — `releasedAt` is the
 * human sign-off that settles it, and nothing ever clears it. Deliberately
 * not keyed on `withheld` (recomputed live on every admin save):
 * `reviewRequired` latches "this ever tripped a trigger" so a later clean
 * retake can't re-roll the gate away.
 */
export const isHtsReviewPending = (profile: {
  reviewRequired: boolean;
  releasedAt: Date | string | null;
}): boolean => profile.reviewRequired && profile.releasedAt === null;

/**
 * What a candidate may see of their own submitted Readiness Profile — every
 * candidate read path must go through here.
 *
 * An access-code guest (see `User.guestCreatedAt`) never sees theirs, released
 * or not, and with or without a profile computed yet: their results go to the
 * requesting agency, same as `isResultsWithheld` (assessment.ts) does for a
 * guest's Assessment sittings. A regular user is gated only by
 * isHtsReviewPending.
 */
export const getHtsCandidateSummaryStatus = (
  profile: { reviewRequired: boolean; releasedAt: Date | string | null } | null,
  isGuest: boolean
): HtsCandidateSummaryStatus => {
  if (isGuest) return HtsSubmissionSummaryStatus.WithheldFromCandidate;
  if (profile && isHtsReviewPending(profile)) return HtsSubmissionSummaryStatus.PendingReview;
  return HtsSubmissionSummaryStatus.Ready;
};

export type HtsCandidateSummaryStatus =
  | typeof HtsSubmissionSummaryStatus.Ready
  | typeof HtsSubmissionSummaryStatus.PendingReview
  | typeof HtsSubmissionSummaryStatus.WithheldFromCandidate;

/**
 * A pillar scoring below this is an area to work on ("Where to focus next"
 * for the candidate; "Focus on" tags for the admin). Shared so the candidate
 * summary, the "Try Again" preselect, and the admin tags can't disagree.
 */
export const HTS_PRIORITY_PILLAR_SCORE_THRESHOLD = 50;

/** How many of a submission's top-scoring pillars are shown as "strengths"/"Strong" — candidate summary and the admin narrative-preview tags share this too. */
export const HTS_STRONG_PILLAR_COUNT = 3;

/** Grade 12 is a minimum requirement, so an entry set aside as not credible never takes Attainment Level below this. */
export const HTS_ATTAINMENT_CREDIBILITY_CAP_SCORE = 25;

/**
 * Caps Attainment Level at HTS_ATTAINMENT_CREDIBILITY_CAP_SCORE when a credibility
 * concern names it. The model reliably reports the entry, but not that a stated
 * level resting on it falls too. Applied even when another listed program is
 * credible: the concern holds the profile for review, and the reviewer confirms the level.
 */
export const applyAttainmentCredibilityCap = (
  scored: { score: number; rationale: string },
  credibilityConcerns: readonly { dimensionKeys?: readonly string[] }[]
): { score: number; rationale: string } => {
  const concerned = credibilityConcerns.some((c) => c.dimensionKeys?.includes(HtsEducationDimensionKey.AttainmentLevel));
  if (!concerned || scored.score <= HTS_ATTAINMENT_CREDIBILITY_CAP_SCORE) return scored;
  return {
    score: HTS_ATTAINMENT_CREDIBILITY_CAP_SCORE,
    rationale: `${scored.rationale} Capped at ${HTS_ATTAINMENT_CREDIBILITY_CAP_SCORE} because a listed program was reported as not credible; a reviewer should confirm the level from the remaining entries.`,
  };
};

/** Any Marginally unsuitable or Unsuitable disclosure caps Integrity's Insight & Accountability at this score. */
export const HTS_INSIGHT_CAP_SCORE = 25;

/**
 * Caps Insight & Accountability at HTS_INSIGHT_CAP_SCORE when any answer is Marginally
 * unsuitable or Unsuitable. Reads classifications, not Conduct History's score, so it
 * survives a change to that scale.
 */
export const applyInsightAccountabilityCap = (
  scored: { score: number; rationale: string },
  suitabilityAnswers: Pick<HtsSuitabilityAnswerForScoring, "classification">[]
): { score: number; rationale: string } => {
  const serious = suitabilityAnswers.some(
    (a) =>
      a.classification === HtsAnswerClassification.MARGINALLY_UNSUITABLE ||
      a.classification === HtsAnswerClassification.UNSUITABLE
  );
  if (!serious || scored.score <= HTS_INSIGHT_CAP_SCORE) return scored;
  return {
    score: HTS_INSIGHT_CAP_SCORE,
    rationale: `${scored.rationale} Capped at ${HTS_INSIGHT_CAP_SCORE} because at least one disclosure was classified Marginally unsuitable or Unsuitable.`,
  };
};

/** Derived deterministically from classification — never trusted from the model directly. */
export const derivePriority = (classification: HtsAnswerClassification): HtsFlagPriority | null => {
  if (classification === HtsAnswerClassification.UNSUITABLE) return HtsFlagPriority.HIGH;
  if (classification === HtsAnswerClassification.MARGINALLY_UNSUITABLE) return HtsFlagPriority.STANDARD;
  return null;
};

export interface HtsSuitabilityAnswerForScoring {
  classification: HtsAnswerClassification | null;
  reviewFlagged: boolean;
  followUpRound: number;
}

export interface HtsEffectiveAnswerFlag {
  effectivePriority: HtsFlagPriority | null;
  effectiveReviewFlagged: boolean;
}

/** The columns `resolveEffectiveAnswerFlag` needs — none are encrypted, so every caller's answer shape already satisfies it. */
export interface HtsAnswerFlagColumns {
  classification: HtsAnswerClassification | null;
  overrideClassification: HtsAnswerClassification | null;
  reviewFlagged: boolean;
  flagPriority: HtsFlagPriority | null;
  overrideFlagged: boolean | null;
  overridePriority: HtsFlagPriority | null;
}

/**
 * One answer's effective (post-override) flag state, branching on the
 * mechanism that produced it — a suitability classification's derived
 * priority, or a hard gate's own flag and severity. Null if the question is
 * neither.
 *
 * Shared rather than API-local because the admin review page renders off it
 * too, and its own copy disagreed on every `unresolvedAtCap` answer.
 */
export const resolveEffectiveAnswerFlag = (
  answer: HtsAnswerFlagColumns,
  question: { classificationTopic: string | null; hardGateRule: unknown }
): HtsEffectiveAnswerFlag | null => {
  if (isSuitabilityTopic(question.classificationTopic)) {
    const effectiveClassification = answer.overrideClassification ?? answer.classification;
    // An admin's reclassification re-derives the flag from the new
    // classification. Absent an override the persisted columns stand and are
    // NOT re-derived: the follow-up engine can flag an answer unresolved at
    // the round cap regardless of classification (`unresolvedAtCap` in
    // htsSuitabilityFollowUp.ts), a state `derivePriority` alone can't express.
    const isOverridden = answer.overrideClassification !== null;
    const derivedPriority = effectiveClassification !== null ? derivePriority(effectiveClassification) : null;
    const effectiveReviewFlagged = isOverridden ? derivedPriority !== null : answer.reviewFlagged;
    const effectivePriority = effectiveReviewFlagged ? (isOverridden ? derivedPriority : answer.flagPriority) : null;
    return { effectivePriority, effectiveReviewFlagged };
  }
  if (question.hardGateRule) {
    const effectiveFlagged = answer.overrideFlagged ?? answer.reviewFlagged;
    const effectivePriority = effectiveFlagged ? (answer.overridePriority ?? answer.flagPriority) : null;
    return { effectivePriority, effectiveReviewFlagged: effectiveFlagged };
  }
  return null;
};

/**
 * Integrity's `Conduct History` dimension, derived deterministically from the
 * suitability classification/flag/follow-up data already persisted on each
 * answer — never re-derived by a model, per the rubric's own design.
 */
export const computeConductHistory = (
  suitabilityAnswers: HtsSuitabilityAnswerForScoring[],
  perfectPersonAnswered: boolean
): { score: number; rationale: string } => {
  if (suitabilityAnswers.some((a) => a.classification === HtsAnswerClassification.UNSUITABLE)) {
    const label = HTS_ANSWER_CLASSIFICATION_LABELS[HtsAnswerClassification.UNSUITABLE];
    return { score: 0, rationale: `At least one disclosure classified ${label}.` };
  }
  if (suitabilityAnswers.some((a) => a.classification === HtsAnswerClassification.MARGINALLY_UNSUITABLE)) {
    const label = HTS_ANSWER_CLASSIFICATION_LABELS[HtsAnswerClassification.MARGINALLY_UNSUITABLE];
    return { score: 25, rationale: `At least one disclosure classified ${label}.` };
  }
  if (suitabilityAnswers.some((a) => a.reviewFlagged)) {
    return { score: 50, rationale: "A disclosure remained unresolved after the maximum follow-up rounds." };
  }
  const anyDisclosure = suitabilityAnswers.some((a) => a.followUpRound > 0);
  if (!anyDisclosure) {
    return perfectPersonAnswered
      ? { score: 100, rationale: "No conduct concerns disclosed, confirmed by the perfect-person probe." }
      : { score: 75, rationale: "No conduct concerns disclosed yet; the perfect-person probe hasn't run." };
  }
  return { score: 75, rationale: "Minor, dated, isolated items only, resolved cleanly through follow-up." };
};

/** Integrity's `Honesty & Completeness of Disclosure` dimension — same deterministic derivation as Conduct History above. */
export const computeHonestyCompleteness = (
  suitabilityAnswers: HtsSuitabilityAnswerForScoring[],
  hasUnresolvedConsistencyFlag: boolean
): { score: number; rationale: string } => {
  if (hasUnresolvedConsistencyFlag) {
    return {
      score: 25,
      rationale: "An unresolved consistency-check flag caps this dimension until a human resolves it.",
    };
  }
  if (
    suitabilityAnswers.some(
      (a) => a.reviewFlagged && a.classification === HtsAnswerClassification.MARGINALLY_SUITABLE
    )
  ) {
    return { score: 25, rationale: "A disclosure was still vague or evasive after the maximum follow-up rounds." };
  }
  const totalRounds = suitabilityAnswers.reduce((sum, a) => sum + a.followUpRound, 0);
  const avgRounds = suitabilityAnswers.length > 0 ? totalRounds / suitabilityAnswers.length : 0;
  if (avgRounds === 0) return { score: 100, rationale: "Every disclosure was complete without any follow-up needed." };
  if (avgRounds <= 1) return { score: 75, rationale: "Disclosures were full and forthcoming with minimal probing." };
  return { score: 50, rationale: "Disclosures were complete only after probing pulled details out piece by piece." };
};

/**
 * A dimension's current value: the admin's pinned override if there is one,
 * otherwise the automated score. `score` is the baseline and is never rewritten
 * by an override — the same original-beside-override shape `HtsAnswer` uses for
 * `classification`/`overrideClassification`.
 */
export const effectiveDimensionScore = (dimension: { score: number; overrideScore?: number }): number =>
  dimension.overrideScore ?? dimension.score;

/** A pillar's score is always the weighted sum of its dimensions' current values — never itself a stored/override target. Callers pass scores already resolved, since a staged (not-yet-saved) override isn't in `overrideScore`. */
export const computePillarScore = (dimensions: { score: number; weight: number }[]): number =>
  Math.round(dimensions.reduce((sum, d) => sum + d.score * d.weight, 0));

/** Conduct History's clean-Section-B score — see `computeConductHistory`. */
export const HTS_CONDUCT_HISTORY_CLEAN_SCORE = 100;

/**
 * At or above this, Demonstrated Test Readiness counts as the rubric's
 * documented-official-pass anchor. A band rather than an exact 100 because the
 * dimension is model-scored and the scorer may return values between anchors.
 */
export const HTS_FITNESS_OFFICIAL_PASS_SCORE_FLOOR = 95;

/**
 * Two rubric rules zero a dimension's weight once another dimension has settled
 * what it exists to measure: a clean Conduct History drops Insight &
 * Accountability (same condition `buildPillarSummaryText` drops it on), a
 * documented official pass takes all of Fitness's weight.
 *
 * Derived at every roll-up off *effective* scores rather than stored, so an
 * override moves the weighting and reverting moves it back — storing it strands
 * Fitness, whose zeroed weights have nothing to restore them from.
 *
 * `score` must already be override-resolved, as for `computePillarScore`; an
 * unrecognized `pillarKey` passes through untouched.
 */
export const resolveEffectiveDimensionWeights = <T extends { key: string; score: number; weight: number }>(
  pillarKey: string,
  dimensions: T[]
): T[] => {
  if (pillarKey === HtsReadinessPillarKey.INTEGRITY) {
    const conductHistory = dimensions.find((d) => d.key === HtsIntegrityDimensionKey.ConductHistory);
    if (conductHistory?.score !== HTS_CONDUCT_HISTORY_CLEAN_SCORE) return dimensions;
    const remainingWeight = dimensions
      .filter((d) => d.key !== HtsIntegrityDimensionKey.InsightAccountability)
      .reduce((sum, d) => sum + d.weight, 0);
    // Nothing to re-split onto — leave the weights alone rather than divide by zero.
    if (remainingWeight <= 0) return dimensions;
    return dimensions.map((d) =>
      d.key === HtsIntegrityDimensionKey.InsightAccountability
        ? { ...d, weight: 0 }
        : { ...d, weight: d.weight / remainingWeight }
    );
  }

  if (pillarKey === HtsReadinessPillarKey.FITNESS) {
    const demonstratedTestReadiness = dimensions.find(
      (d) => d.key === HtsFitnessDimensionKey.DemonstratedTestReadiness
    );
    if ((demonstratedTestReadiness?.score ?? 0) < HTS_FITNESS_OFFICIAL_PASS_SCORE_FLOOR) return dimensions;
    return dimensions.map((d) => ({
      ...d,
      weight: d.key === HtsFitnessDimensionKey.DemonstratedTestReadiness ? 1 : 0,
    }));
  }

  return dimensions;
};

/** A pillar's score with the weight rules above applied — every roll-up path goes through here, so none of them can weight a pillar differently from the others. */
export const computePillarScoreWithWeightRules = (
  pillarKey: string,
  dimensions: { key: string; score: number; weight: number }[]
): number => computePillarScore(resolveEffectiveDimensionWeights(pillarKey, dimensions));

/**
 * Weighted average over the pillars actually scored, by each one's own
 * `compositeWeight` (default 1). `key` is plain `string`, not the
 * HtsReadinessPillarKey union, so an unrecognized key falls back to 1
 * rather than throwing.
 */
export const computeCompositeScore = (pillarScores: { pillarScore: number; key: string }[]): number => {
  const weightOf = (key: string): number => HTS_READINESS_PILLAR_CONFIG_BY_KEY[key]?.compositeWeight ?? 1;
  const totalWeight = pillarScores.reduce((sum, p) => sum + weightOf(p.key), 0);
  return Math.round(pillarScores.reduce((sum, p) => sum + p.pillarScore * weightOf(p.key), 0) / totalWeight);
};

/**
 * Each present pillar's share of the composite score (the `compositeWeight`
 * split `computeCompositeScore` uses), as a percentage to one decimal.
 *
 * Plain rounding on purpose: equal-weight pillars always show equal
 * percentages, even if the total lands slightly off 100 (e.g. 10.5% × 9 +
 * 5.3% = 99.8%).
 */
export const computePillarWeightPercents = (presentPillars: string[]): Map<string, number | null> => {
  const weights = presentPillars.map((key) => ({
    key,
    weight: HTS_READINESS_PILLAR_CONFIG_BY_KEY[key]?.compositeWeight ?? 1,
  }));
  const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0);
  return new Map(
    weights.map(({ key, weight }) => [key, totalWeight > 0 ? Math.round((weight / totalWeight) * 1000) / 10 : null])
  );
};
